import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import {
  toWidget,
  toWidgetEditable,
} from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import Command from '@ckeditor/ckeditor5-core/src/command';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';

const INSERT_COMMAND = 'insertLibraryItem';
const INIT_CALLBACK_COMMAND = 'initLibraryItemCallback';

function createLibraryItemBox(writer, id, data) {
  const linkedObject = writer.createElement('linkedObject');
  const linkedObjectPlaceholder = writer.createElement(
    'linkedObjectPlaceholder',
    {
      id,
      data,
    }
  );
  writer.append(linkedObjectPlaceholder, linkedObject);
  return linkedObject;
}

class InitCallbackCommand extends Command {
  execute(callback) {
    if (callback) {
      this.editor.__callback = callback;
    }
  }
}

class InsertLibraryItemCommand extends Command {
  execute(id, data, callback) {
    if (callback) {
      this.editor.__callback = callback;
    }

    this.editor.model.change((writer) => {
      this.editor.model.insertContent(createLibraryItemBox(writer, id, data));
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const allowedIn = model.schema.findAllowedParent(
      selection.getFirstPosition(),
      'linkedObject'
    );

    this.isEnabled = allowedIn !== null;
  }
}

class LibraryItemBoxEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();

    this.editor.commands.add(
      INSERT_COMMAND,
      new InsertLibraryItemCommand(this.editor)
    );
    this.editor.commands.add(
      INIT_CALLBACK_COMMAND,
      new InitCallbackCommand(this.editor)
    );
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register('linkedObject', {
      // Behaves like a self-contained object (e.g. an image).
      isObject: true,

      // Allow in places where other blocks are allowed (e.g. directly in the root).
      allowWhere: '$block',
    });

    schema.register('linkedObjectPlaceholder', {
      // Cannot be split or left by the caret.
      isLimit: true,

      allowIn: 'linkedObject',

      // Allow content which is allowed in blocks (i.e. text with attributes).
      allowContentOf: '$root',

      // Need to enable id attribute for writer.createElement('linkedObjectPlaceholder', {id});
      allowAttributes: ['id', 'data'],
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;

    // <linkedObject> converters
    conversion.for('upcast').elementToElement({
      model: 'linkedObject',

      view: {
        name: 'div',
        classes: 'linked-object',
      },
    });
    conversion.for('dataDowncast').elementToElement({
      model: 'linkedObject',
      view: {
        name: 'div',
        classes: 'linked-object',
      },
    });
    conversion.for('editingDowncast').elementToElement({
      model: 'linkedObject',
      view: (modelElement, { writer: viewWriter }) => {
        const section = viewWriter.createContainerElement('div', {
          class: 'linked-object',
        });

        return toWidget(section, viewWriter, { label: 'simple box widget' });
      },
    });

    // <linkedObjectPlaceholder> converters
    conversion.for('upcast').elementToElement({
      model: (modelElement, { writer }) => {
        const value = modelElement.getAttribute('id');
        const data = modelElement.getAttribute('data');
        return writer.createElement('linkedObjectPlaceholder', {
          id: value,
          data,
        });
      },
      view: {
        name: 'div',
        classes: 'linked-object-placeholder',
      },
    });
    conversion.for('dataDowncast').elementToElement({
      model: 'linkedObjectPlaceholder',

      view: (modelElement, { writer }) => {
        const id = modelElement.getAttribute('id').toString();
        const data = modelElement.getAttribute('data');
        const div = writer.createContainerElement('div', {
          class: 'linked-object-placeholder',
          id,
          ...(data.render && { 'data-render': data.render }),
          ...(data && { data: btoa(JSON.stringify(data)) }),
        });

        return toWidgetEditable(div, writer, {
          label: 'linkedObjectPlaceholder widget',
        });
      },
    });
    conversion.for('editingDowncast').elementToElement({
      model: 'linkedObjectPlaceholder',
      view: (modelElement, { writer: viewWriter }) => {
        const id = modelElement.getAttribute('id').toString();
        let data = modelElement.getAttribute('data');

        if (typeof data === 'string') {
          data = JSON.parse(atob(data));
        }

        const div = viewWriter.createRawElement(
          'div',
          {
            id,
          },
          (domElement) => {
            this.editor.__callback && this.editor.__callback(domElement);
          }
        );

        return toWidgetEditable(div, viewWriter);
      },
    });
  }
}

class LibraryItemBoxUI extends Plugin {
  init() {
    const editor = this.editor;
    const t = editor.t;

    // The "linkedObject" button must be registered among the UI components of the editor
    // to be displayed in the toolbar.
    editor.ui.componentFactory.add('linkedObject', (locale) => {
      // The state of the button will be bound to the widget command.
      const command = editor.commands.get(INSERT_COMMAND);

      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);

      buttonView.set({
        // The t() function helps localize the editor. All strings enclosed in t() can be
        // translated and change when the language of the editor changes.
        label: t('Insert Library Item'),
        withText: true,
        tooltip: true,
      });

      // Bind the state of the button to the command.
      buttonView.bind('isOn', 'isEnabled').to(command, 'value', 'isEnabled');

      // Execute the command when the button is clicked (executed).
      this.listenTo(buttonView, 'execute', () =>
        editor.execute(INSERT_COMMAND)
      );

      return buttonView;
    });
  }
}

class LibraryItemBox extends Plugin {
  static get requires() {
    return [LibraryItemBoxEditing, LibraryItemBoxUI];
  }
}

export default LibraryItemBox;
